import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { FiEdit2, FiInfo } from "react-icons/fi";
import { vocabularize } from "src/Common/vocab";

import { can, handleError } from "src/Common/function";
import { getAvatarUrl, getImageUrl } from "src/Common/common";
import FormTemplateService from "src/Services/API/formtemplate.service";
import { getFormTypeLabel, getFrequencyLabel, matchFrequencies } from "src/Pages/forms/formEnums";
import Highlight from "src/Components/Common/Highlight";
import { FormIcon } from "src/Pages/forms/formIcons";
import Button from "src/Components/Button";
import CreateButton from "src/Components/CreateButton";
import ConfirmDialog from "src/Common/confirmdialog";
import CreateTemplateModal from "./CreateTemplateModal";
import FormTemplateFilterView from "./components/FormTemplateFilterView";
import FormCreateModal from "src/Pages/WO/Components/forms/FormCreateModal";
import FormViewerModal from "src/Pages/WO/Components/forms/FormViewerModal";
import { ActionMenu, ActionMenuIcon, ActionMenuItem } from "src/Components/Common/ActionMenu";
import UserService from "src/Services/API/user.service";
import AddTemplateModal from "./components/AddTemplateModal";
import FormTypeManagerModal from "./components/FormTypeManagerModal";
import StandardBranchesModal from "./components/StandardBranchesModal";


function TabButton({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        color: active ? "#0058E5" : "#7C829A",
        fontFamily: "OpenSansRegular",
        fontSize: "14px",
        fontWeight: 600,
        lineHeight: "20px",
        letterSpacing: "0.14px",
        textTransform: "capitalize",
      }}
      className="relative py-4 text-center"
    >
      {label}
      {active && <span className="absolute left-0 -bottom-px w-full h-[2px] bg-blueColor" />}
    </button>
  );
}

/**
 * Standard Forms toggle for the row action menu. Filled = the template is in the
 * library and available on every work order; outline = it is not. The menu is a
 * column of 15px glyphs, so this is a glyph rather than a labelled item.
 */
// The branches a template is standard in, for the column and the badge tooltip.
// There is no implicit "all", so this is always a concrete list.
const standardBranchNames = (template) => {
  const names = (template.standardBranches || []).map((branch) => branch.name);
  return names.length ? names.join(", ") : "—";
};

function StandardStar({ active }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill={active ? "#0058E5" : "none"}
      stroke={active ? "#0058E5" : "#7C829A"}
      strokeWidth="2"
      strokeLinejoin="round"
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

const TemplateList = ({ hideTabs = false, hideControls = false, customerId = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Keyword comes from the global SearchBox in the header via the URL, the same
  // way the WO and PO lists read it. This page used to ignore it.
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("keyword") || "";
  const isCustomerContext = customerId !== null && customerId !== undefined;

  const canCreate = can("form:templateCreate");
  const canUpdate = can("form:templateUpdate");
  const canArchive = can("form:templateArchive");
  const canRestore = can("form:templateRestore");
  const canDuplicate = can("form:templateDuplicate");
  const canCreateForm = can("form:create");
  // Putting a template in front of every technician is its own permission,
  // deliberately outside form:* so no wildcard policy grants it.
  const canManageStandard = can("formConfig:manageStandard");
  const canManageTypes = can("formConfig:manageTypes");

  const [tab, setTab] = useState("Active");
  const [templates, setTemplates] = useState([]);
  const [appliedFilterType, setAppliedFilterType] = useState("");
  // "" = every template, "true" = only the Standard Forms library.
  const [standardOnly, setStandardOnly] = useState("");
  const [formTypeManagerOpen, setFormTypeManagerOpen] = useState(false);
  // Read-only preview of a template's contents, opened by clicking its row.
  const [previewTemplate, setPreviewTemplate] = useState(null);
  // Template whose Standard Form branches are being edited.
  const [standardBranchesFor, setStandardBranchesFor] = useState(null);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [addTemplateModalOpen, setAddTemplateModalOpen] = useState(false);
  const [assignableTemplates, setAssignableTemplates] = useState([]);
  const [selectedTemplateForEdit, setSelectedTemplateForEdit] = useState(null);
  const [formCreateModalOpen, setFormCreateModalOpen] = useState(false);
  const [selectedTemplateForForm, setSelectedTemplateForForm] = useState(null);
  const [actionMenuId, setActionMenuId] = useState(null);
  const [confirmTemplateId, setConfirmTemplateId] = useState(null);

  const fetchTemplates = (isActive, formType = appliedFilterType) => {
    const activeParam = isActive !== undefined ? isActive : tab === "Active";
    FormTemplateService.getAll(
      String(activeParam),
      formType !== "" ? formType : "",
      isCustomerContext ? customerId : "",
      {
        keyword: keyword || undefined,
        // Type names are searched server-side; frequency labels are still
        // client-side, so the values the keyword names are resolved here.
        frequencyMatches: keyword ? matchFrequencies(keyword) : undefined,
        isStandard: standardOnly,
      },
    )
      .then((response) => {
        if (response.status === 200) {
          setTemplates(response.data);
        }
      })
      .catch((error) => handleError(error));
  };

  useEffect(() => {
    fetchTemplates(hideTabs ? true : tab === "Active", appliedFilterType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, hideTabs, customerId, appliedFilterType, keyword, standardOnly]);

  // Membership is the branch set, so the star opens the picker rather than
  // toggling: a template is standard in exactly the branches chosen there.
  // Changing it affects availability only — a form a technician has already
  // opened is its own row with its own copy of the schema.
  const handleManageStandard = (template) => {
    setActionMenuId(null);
    setStandardBranchesFor(template);
  };

  const handleTabClick = (nextTab) => {
    setTab(nextTab);
    setActionMenuId(null);
  };

  const handleApplyFilter = ({ filterType }) => {
    setAppliedFilterType(filterType);
  };

  const handleCreate = (data) => {
    const isEditing = selectedTemplateForEdit !== null;
    setCreateModalOpen(false);
    setAddTemplateModalOpen(false);
    navigate("/form-builder", {
      state: {
        mode: "template",
        templateDraft: data,
        templateName: data.name,
        ...(isEditing
          ? {
              templateId: selectedTemplateForEdit.id,
              formSchema: selectedTemplateForEdit.schema ?? null,
            }
          : {}),
        customerId: isCustomerContext ? customerId : undefined,
        from: isCustomerContext ? location.pathname : "/form-templates",
      },
    });
    setSelectedTemplateForEdit(null);
  };

  // Save just the template's details — name, description, type, frequency —
  // without the trip through the form builder. Editing the type is the common
  // case and had no way to persist on its own.
  const handleSaveTemplateDetails = (data) => {
    if (!selectedTemplateForEdit) return;
    FormTemplateService.update(selectedTemplateForEdit.id, data)
      .then(() => {
        toast("Template saved");
        setCreateModalOpen(false);
        setSelectedTemplateForEdit(null);
        fetchTemplates(hideTabs ? true : tab === "Active");
      })
      .catch((error) => handleError(error));
  };

  const handleAssignTemplate = (templateId) => {
    if (!isCustomerContext || !templateId) return;
    FormTemplateService.update(templateId, { customerId })
      .then(() => {
        toast("Template assigned");
        setAddTemplateModalOpen(false);
        fetchTemplates(hideTabs ? true : tab === "Active", appliedFilterType);
      })
      .catch((error) => handleError(error));
  };

  const openAddTemplateModal = () => {
    if (!isCustomerContext) {
      setCreateModalOpen(true);
      return;
    }

    FormTemplateService.getAll("true", "", "")
      .then((response) => {
        if (response.status === 200) {
          setAssignableTemplates(response.data.filter((t) => !t.customer));
        }
        setAddTemplateModalOpen(true);
      })
      .catch((error) => handleError(error));
  };

  const handleArchive = (id) => {
    FormTemplateService.archive(id)
      .then(() => {
        toast("Template archived");
        setActionMenuId(null);
        fetchTemplates(tab === "Active", appliedFilterType);
      })
      .catch((error) => handleError(error));
  };

  const handleRestore = (id) => {
    FormTemplateService.restore(id)
      .then(() => {
        toast("Template restored");
        setActionMenuId(null);
        fetchTemplates(tab === "Active", appliedFilterType);
      })
      .catch((error) => handleError(error));
  };

  const handleEdit = (template) => {
    setSelectedTemplateForEdit(template);
    setCreateModalOpen(true);
    setActionMenuId(null);
  };

  const handleDuplicate = (template) => {
    const currentUser = UserService.getCurrentUser();

    FormTemplateService.duplicate(template.id, currentUser?.id)
      .then(() => {
        toast("Template duplicated");
        setActionMenuId(null);
        fetchTemplates(tab === "Active", appliedFilterType);
      })
      .catch((error) => handleError(error));
  };

  const handlePlusAction = (template) => {
    setSelectedTemplateForForm(template);
    setFormCreateModalOpen(true);
    setActionMenuId(null);
  };

  const requestArchiveRestore = (id) => {
    setConfirmTemplateId(id);
    setActionMenuId(null);
  };

  const handleConfirmArchiveRestore = () => {
    if (confirmTemplateId === null) return;
    if (tab === "Active") {
      handleArchive(confirmTemplateId);
    } else {
      handleRestore(confirmTemplateId);
    }
    setConfirmTemplateId(null);
  };

  return (
    <div className="w-full h-full flex flex-col min-h-0 bg-backgroundColor">
      <ConfirmDialog
        title={tab === "Active" ? "Archive Template" : "Restore Template"}
        description={
          tab === "Active"
            ? "Are you sure you want to archive this template?"
            : "Are you sure you want to restore this template?"
        }
        shown={confirmTemplateId !== null}
        onConfirmClicked={handleConfirmArchiveRestore}
        onCancelClicked={() => setConfirmTemplateId(null)}
      />

      <CreateTemplateModal
        shown={createModalOpen}
        mode={selectedTemplateForEdit ? "edit" : "create"}
        initialData={selectedTemplateForEdit}
        onCancelClicked={() => {
          setCreateModalOpen(false);
          setSelectedTemplateForEdit(null);
        }}
        onSubmitClicked={handleCreate}
        onSaveClicked={handleSaveTemplateDetails}
      />
      <AddTemplateModal
        shown={addTemplateModalOpen}
        templates={assignableTemplates}
        onClose={() => setAddTemplateModalOpen(false)}
        onAssignTemplate={handleAssignTemplate}
        onCreateTemplate={() => {
          setAddTemplateModalOpen(false);
          setCreateModalOpen(true);
        }}
      />
      <FormCreateModal
        shown={formCreateModalOpen}
        onClose={() => {
          setFormCreateModalOpen(false);
          setSelectedTemplateForForm(null);
        }}
        initialTemplateId={selectedTemplateForForm?.id ?? null}
        initialTemplateSchema={selectedTemplateForForm?.schema ?? null}
        initialFormType={selectedTemplateForForm?.formType ?? null}
        initialFrequency={
          selectedTemplateForForm?.frequency !== null && selectedTemplateForForm?.frequency !== undefined
            ? String(selectedTemplateForForm.frequency)
            : ""
        }
      />

      {!hideControls && (
        <div className="w-full flex items-center justify-between px-[18px] border-b border-borderColor">
          <div className="flex items-center gap-6">
            {!hideTabs && (
              <>
                <TabButton
                  label="All"
                  active={tab === "Active" && standardOnly === ""}
                  onClick={() => {
                    setStandardOnly("");
                    handleTabClick("Active");
                  }}
                />
                <TabButton
                  label="Standard Templates"
                  active={standardOnly === "true"}
                  onClick={() => {
                    setStandardOnly("true");
                    handleTabClick("Active");
                  }}
                />
                <TabButton
                  label="Archived"
                  active={tab === "Archived"}
                  onClick={() => {
                    setStandardOnly("");
                    handleTabClick("Archived");
                  }}
                />
              </>
            )}
          </div>
          {/* pr-[10px] lines the CREATE button's right edge up with the table's
              right border below: the header sits at px-[18px], the table at
              px-7 (28px). */}
          <div className="flex items-center gap-3 pr-[10px]">
            {canManageTypes && (
              <Button
                onClicked={() => setFormTypeManagerOpen(true)}
                title="Form Types"
                icon={<FiEdit2 size={12} />}
              />
            )}
            {canCreate && (
              <CreateButton isPrimary onClicked={() => setCreateModalOpen(true)} title="CREATE" />
            )}
          </div>
        </div>
      )}

      <div className={`flex-1 min-h-0 flex flex-col ${hideControls ? "" : "px-7 mt-5"}`}>
        {/* What "Standard" means is not guessable from a list of templates, and
            the consequence — every technician sees these on every job — is worth
            stating where someone is about to add one. */}
        {!hideControls && standardOnly === "true" && (
          <div className="flex items-start gap-2 mb-3 px-3 py-2 rounded-[6px] bg-[#EAF3FF] border border-[#CFE2FF]">
            <FiInfo className="mt-[1px] shrink-0 text-[#0058E5]" size={14} />
            <p className="font-OpenSansRegular text-[12px] leading-[18px] text-[#1A2332]">
              These templates are available automatically on every {vocabularize("work order")} in
              the branches listed against them — a technician can open one in the field whenever they
              need it, without anyone assigning it first. Availability is per branch: a form appears
              only in the branches you pick, never everywhere by default.
            </p>
          </div>
        )}

        {/* Always on screen rather than behind a toggle — see AllForms. */}
        {!hideControls && (
          <div className="relative z-20">
            <FormTemplateFilterView onApply={handleApplyFilter} />
          </div>
        )}

        <div
          className="w-full mt-3 mb-3 flex-1 min-h-0 flex flex-col overflow-hidden"
          style={{
            borderRadius: "12px",
            border: "1px solid #E5E8F3",
            background: "#FFF",
            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.00), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
          }}
        >
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-auto">
            <table className="w-full min-w-[1300px]">
              <thead className="sticky top-0 z-10">
                <tr
                  style={{ borderBottom: "1px solid #E5E8F3", background: "#F8F9FE" }}
                  className="[&>th]:border-r [&>th]:border-borderColor [&>th:last-child]:border-r-0"
                >
                  <th>
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center justify-center w-[56px] h-[44px] text-[#5F6477]">
                      No
                    </div>
                  </th>
                  <th>
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[320px] h-[44px] text-[#5F6477]">
                      Template Name
                    </div>
                  </th>
                  <th className="min-w-[200px] w-full max-w-[380px]">
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 h-[44px] text-[#5F6477]">
                      Description
                    </div>
                  </th>
                  <th>
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[200px] h-[44px] text-[#5F6477]">
                      Type
                    </div>
                  </th>
                  <th>
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[180px] h-[44px] text-[#5F6477]">
                      Frequency
                    </div>
                  </th>
                  <th>
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[180px] h-[44px] text-[#5F6477]">
                      Created by
                    </div>
                  </th>
                  {standardOnly === "true" && (
                    <th>
                      <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[220px] h-[44px] text-[#5F6477]">
                        Branches
                      </div>
                    </th>
                  )}
                  <th>
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center justify-center w-[140px] h-[44px] text-[#5F6477]">
                      Date Created
                    </div>
                  </th>
                  <th>
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[20px] h-[44px] text-[#5F6477]"></div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {templates.map((template, index) => (
                  <tr
                    key={template.id}
                    onClick={() => setPreviewTemplate(template)}
                    className="border-b border-borderColor cursor-pointer hover:bg-[#FAFAFB] [&>td]:border-r [&>td]:border-borderColor [&>td:last-child]:border-r-0"
                  >
                    <td>
                      <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332] text-center">
                        {templates.length - index}
                      </div>
                    </td>
                    <td>
                      <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332] word-break-break-word flex items-center gap-2">
                        {template.icon && (
                          <span className="shrink-0 flex items-center">
                            <FormIcon iconKey={template.icon} size={17} />
                          </span>
                        )}
                        <span>
                          <Highlight text={template.name} keyword={keyword} />
                        </span>
                        {template.isStandard && (
                          <span
                            className="shrink-0 rounded-full px-2 py-[2px] text-[10px] font-OpenSansRegular font-[600] bg-[#EAF3FF] text-[#0058E5]"
                            title={standardBranchNames(template)}
                          >
                            Standard
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332] word-break-break-word">
                        <Highlight text={template.description} keyword={keyword} />
                      </div>
                    </td>
                    <td>
                      <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332]">
                        <Highlight text={getFormTypeLabel(template)} keyword={keyword} />
                      </div>
                    </td>
                    <td>
                      <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332]">
                        {template.frequency !== null && template.frequency !== undefined
                          ? getFrequencyLabel(template.frequency)
                          : "-"}
                      </div>
                    </td>
                    <td>
                      <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332] flex items-center gap-2">
                        {template.createdBy?.avatar && (
                          <img
                            src={getAvatarUrl(template.createdBy.avatar)}
                            className="w-[20px] h-[20px] rounded-[10px] object-contain"
                          />
                        )}
                        <span>{template.createdBy?.name}</span>
                      </div>
                    </td>
                    {standardOnly === "true" && (
                      <td>
                        <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332] word-break-break-word">
                          {standardBranchNames(template)}
                        </div>
                      </td>
                    )}
                    <td>
                      <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332] text-center">
                        {moment(template.createdAt).format("MM/DD/YYYY")}
                      </div>
                    </td>
                    <td className="relative" onClick={(e) => e.stopPropagation()}>
                      <img
                        onClick={() => setActionMenuId(actionMenuId === template.id ? null : template.id)}
                        className="cursor-pointer"
                        src={getImageUrl("/images/common/options.png")}
                        alt="Options"
                      />
                      {actionMenuId === template.id && (
                        <ActionMenu onClose={() => setActionMenuId(null)}>
                            {tab === "Active" && (
                              <>
                                {canCreateForm && (
                                  <ActionMenuIcon
                                    onClick={() => handlePlusAction(template)}
                                    title="Create Form"
                                    src={getImageUrl("/images/common/white_plus.png")}
                                  />
                                )}
                                {canUpdate && (
                                  <ActionMenuIcon
                                    onClick={() => handleEdit(template)}
                                    title="Edit"
                                    src={getImageUrl("/images/common/edit.png")}
                                  />
                                )}
                                {canDuplicate && (
                                  <ActionMenuIcon
                                    onClick={() => handleDuplicate(template)}
                                    title="Duplicate"
                                    src={getImageUrl("/images/common/white_duplicate.png")}
                                  />
                                )}
                                {canManageStandard && (
                                  <ActionMenuItem
                                    onClick={() => handleManageStandard(template)}
                                    title={
                                      template.isStandard
                                        ? "Standard Form branches"
                                        : "Make a Standard Form"
                                    }
                                  >
                                    <StandardStar active={!!template.isStandard} />
                                  </ActionMenuItem>
                                )}
                              </>
                            )}
                            {((tab === "Active" && canArchive) || (tab !== "Active" && canRestore)) && (
                              <ActionMenuIcon
                                onClick={() => requestArchiveRestore(template.id)}
                                title={tab === "Active" ? "Archive" : "Restore"}
                                src={getImageUrl(
                                  tab === "Active"
                                    ? "/images/common/white_archive.png"
                                    : "/images/common/white_repeat.png",
                                )}
                              />
                            )}
                        </ActionMenu>
                      )}
                    </td>
                  </tr>
                ))}
                {templates.length === 0 && (
                  <tr>
                    <td colSpan={standardOnly === "true" ? 9 : 8} className="text-center py-10 text-[12px] font-OpenSansRegular text-subTitleColor">
                      No templates found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {hideControls && (canCreate || canUpdate) && (
          <div className="w-full flex items-center justify-center py-4">
            <CreateButton onClicked={openAddTemplateModal} title="ADD TEMPLATE" />
          </div>
        )}
      </div>
    {formTypeManagerOpen && <FormTypeManagerModal onClose={() => setFormTypeManagerOpen(false)} />}

      {standardBranchesFor && (
        <StandardBranchesModal
          template={standardBranchesFor}
          onClose={() => setStandardBranchesFor(null)}
          onSaved={() => {
            setStandardBranchesFor(null);
            fetchTemplates(hideTabs ? true : tab === "Active");
          }}
        />
      )}

      {/* A template has no work order and so no branch of its own. If it is a
          Standard Form, its first branch stands in so the logo renders as it
          will in the field; the note says whose logo is on screen, since another
          branch's copy of the same form will look different. */}
      <FormViewerModal
        shown={!!previewTemplate}
        title={previewTemplate?.name || "Template Preview"}
        forms={previewTemplate ? [previewTemplate] : []}
        allowUnassign={false}
        fallbackBranchId={previewTemplate?.standardBranches?.[0]?.id || null}
        note={
          previewTemplate?.standardBranches?.[0]?.name
            ? `Preview — showing the ${previewTemplate.standardBranches[0].name} logo`
            : null
        }
        onClose={() => setPreviewTemplate(null)}
      />
    </div>
  );
};

export default TemplateList;
